<template>
  <div class="classes">
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <span>类别注册表</span>
        <div class="table-header-right">
          <span class="total-count">共 {{ classes.length }} 个，启用 {{ enabledCount }} 个</span>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">新增类别</el-button>
        </div>
      </div>

      <div class="page-hint">
        这里是类别的唯一真源：预标注默认清单、导出重映射、新建项目标签模板、训练 data.yaml、评估分组都按它走。
        加完新类别，记得在下面把它并进在用的标注项目。
      </div>

      <el-table
        v-loading="loading"
        :data="classes"
        :empty-text="emptyText"
        stripe
        border
        size="small"
        style="width:100%"
      >
        <el-table-column prop="sort_order" label="序号" width="70" align="center" />
        <el-table-column label="类别" min-width="130" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span class="class-name">{{ row.name }}</span>
          </template>
        </el-table-column>
        <el-table-column label="提示词" min-width="220" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span v-if="row.prompt" class="ellipsis-cell" :title="row.prompt">{{ row.prompt }}</span>
            <span v-else class="muted">默认用类别名</span>
          </template>
        </el-table-column>
        <el-table-column label="颜色" width="90" align="center">
          <template slot-scope="{ row }">
            <span v-if="row.color" class="color-cell">
              <span class="color-dot" :style="{ background: row.color }" />
              {{ row.color }}
            </span>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="业务组" width="110" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.business_group" size="mini" type="info">{{ groupLabel(row.business_group) }}</el-tag>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.enabled" size="mini" type="success">启用</el-tag>
            <el-tag v-else size="mini" type="info">已停用</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openEdit(row)">编辑</el-button>
            <el-button
              type="text"
              size="mini"
              :loading="togglingId === row.id"
              @click="toggleEnabled(row)"
            >{{ row.enabled ? '停用' : '启用' }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新类别与提示词改动要进在用项目的标签，否则预标注派发会因「类别不在项目里」整批失败 -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <span>标注项目标签同步</span>
        <div class="table-header-right">
          <el-button size="small" :loading="projectsLoading" @click="loadProjects">刷新</el-button>
        </div>
      </div>
      <div class="page-hint">
        同步＝把注册表启用类别并进项目标签，只增不删（已标过的类别不会被抽走，颜色按注册表更新）。
      </div>
      <el-table
        v-loading="projectsLoading"
        :data="projects"
        :empty-text="projectsEmptyText"
        stripe
        border
        size="small"
        style="width:100%"
      >
        <el-table-column label="标注项目" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.title }}</template>
        </el-table-column>
        <el-table-column label="已有条数" width="110" align="center">
          <template slot-scope="{ row }">{{ row.task_number || 0 }}</template>
        </el-table-column>
        <el-table-column label="操作" width="140" align="center">
          <template slot-scope="{ row }">
            <el-button
              type="text"
              size="mini"
              :loading="syncingId === row.id"
              @click="syncProject(row)"
            >同步标签配置</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- append-to-body：本组件挂在弹窗里使用，内层表单弹窗若留在外层弹窗 DOM 内，
         会落到外层面罩之下（看得见但点不动） -->
    <el-dialog
      :title="form.id ? `编辑类别 — ${form.name}` : '新增类别'"
      :visible.sync="formVisible"
      width="520px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form label-width="90px" size="small">
        <el-form-item label="类别名">
          <el-input v-model="form.name" :disabled="!!form.id" placeholder="字母、数字或下划线" />
          <div class="form-hint">
            {{ form.id ? '名字与序号不可改（改名会让历史标注全错）' : '落库后名字不可改；序号自动排到最后，即 YOLO 类别 id' }}
          </div>
        </el-form-item>
        <el-form-item label="提示词">
          <el-input
            v-model="form.prompt"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 6 }"
            placeholder="留空用类别名，多备选用 | 分隔"
          />
          <div class="form-hint">预标注时按这些词去框，例：<span class="mono">行人 | 骑车人</span>；可换行编辑</div>
        </el-form-item>
        <el-form-item label="颜色">
          <el-color-picker v-model="form.color" />
          <span class="form-hint">画框颜色，新建项目与同步标签时带上</span>
        </el-form-item>
        <el-form-item label="业务组">
          <el-select v-model="form.business_group" placeholder="选择业务组" style="width: 100%">
            <el-option label="动态目标（dynamic）" value="dynamic" />
            <el-option label="静态目标（static）" value="static" />
          </el-select>
          <div class="form-hint">评估按它分组算指标</div>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.enabled" />
          <span class="form-hint">停用后新版本/导出不再包含该类，历史版本和已有标注不受影响</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="formVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="save">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchDatasetClasses, createDatasetClass, updateDatasetClass,
  fetchLabelStudioProjects, syncDatasetProjectLabelConfig,
} from '../../api/dataset';

/** 类别名规则（与后端同一把尺子：YOLO 语义名，字母/数字/下划线） */
const CLASS_NAME_RE = /^[A-Za-z0-9_]+$/;

/** 业务组中文名（评估分组按它归并） */
const GROUP_LABELS = { dynamic: '动态', static: '静态' };

export default {
  name: 'Classes',

  data() {
    return {
      classes: [],
      loading: false,
      projects: [],
      projectsLoading: false,
      syncingId: null,
      togglingId: null,
      formVisible: false,
      saving: false,
      form: { id: null, name: '', prompt: '', color: null, business_group: 'dynamic', enabled: true },
    };
  },

  computed: {
    enabledCount() {
      return this.classes.filter(c => c.enabled).length;
    },

    emptyText() {
      return this.loading ? '' : '注册表还是空的，点右上角新增第一个类别';
    },

    projectsEmptyText() {
      return this.projectsLoading ? '' : '没有取到标注项目';
    },
  },

  mounted() {
    this.loadClasses();
    this.loadProjects();
  },

  methods: {
    async loadClasses() {
      this.loading = true;
      try {
        // 列表连停用类一起带出来（停用类仍参与导出重映射，只是不进新模板）
        this.classes = ((await fetchDatasetClasses()).data) || [];
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.loading = false;
      }
    },

    async loadProjects() {
      this.projectsLoading = true;
      try {
        this.projects = ((await fetchLabelStudioProjects()).data) || [];
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.projectsLoading = false;
      }
    },

    /** 业务组中文名；注册表里出现没见过的组就原样显示 */
    groupLabel(group) {
      return GROUP_LABELS[group] || group;
    },

    openCreate() {
      this.form = { id: null, name: '', prompt: '', color: null, business_group: 'dynamic', enabled: true };
      this.formVisible = true;
    },

    openEdit(row) {
      this.form = {
        id: row.id,
        name: row.name,
        prompt: row.prompt || '',
        color: row.color || null,
        business_group: row.business_group || 'dynamic',
        enabled: !!row.enabled,
      };
      this.formVisible = true;
    },

    /** 保存：新增与编辑走不同接口（名字与序号一律不可改，编辑只提交改动字段） */
    async save() {
      const f = this.form;
      const payload = {
        name: String(f.name || '').trim(),
        prompt: f.prompt,
        color: f.color || null,
        business_group: f.business_group || null,
        enabled: f.enabled,
      };
      if (!payload.name) {
        this.$message.warning('请填类别名');
        return;
      }
      if (!CLASS_NAME_RE.test(payload.name)) {
        this.$message.warning('类别名只能用字母、数字、下划线');
        return;
      }
      this.saving = true;
      try {
        if (f.id) {
          // name 不提交：改名的口子从一开始就别开
          await updateDatasetClass(f.id, {
            prompt: payload.prompt,
            color: payload.color,
            business_group: payload.business_group,
            enabled: payload.enabled,
          });
          this.$message.success(`已保存 ${payload.name}`);
        } else {
          await createDatasetClass(payload);
          this.$message.success(`已新增 ${payload.name}，记得把在用项目同步一下标签`);
        }
        this.formVisible = false;
        await this.loadClasses();
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.saving = false;
      }
    },

    /** 停用 / 启用：同一个 PATCH，停用前问一句（它决定下一批还预不预标注这个类） */
    async toggleEnabled(row) {
      if (row.enabled) {
        try {
          await this.$confirm(
            `停用后新版本/导出不再包含「${row.name}」，历史版本和已有标注不受影响。`,
            '停用类别',
            { confirmButtonText: '停用', cancelButtonText: '取消', type: 'warning' },
          );
        } catch {
          return; // 用户取消
        }
      }
      this.togglingId = row.id;
      try {
        await updateDatasetClass(row.id, { enabled: !row.enabled });
        this.$message.success(row.enabled ? `已停用 ${row.name}` : `已启用 ${row.name}`);
        await this.loadClasses();
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.togglingId = null;
      }
    },

    /** 把注册表启用类并进项目标签（后端只增不删，返回本次新并入的标签名） */
    async syncProject(row) {
      this.syncingId = row.id;
      try {
        const res = await syncDatasetProjectLabelConfig(row.id);
        const added = (res.data && res.data.added) || [];
        if (added.length > 0) {
          this.$message.success(`已把 ${added.join('、')} 并入「${row.title}」`);
        } else {
          this.$message.success(`「${row.title}」的标签已经是最新的`);
        }
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.syncingId = null;
      }
    },
  },
};
</script>

<style scoped>
.table-card {
  margin-bottom: 16px;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.total-count {
  color: #909399;
  font-size: 13px;
}

.page-hint {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
  margin-bottom: 10px;
}

.muted {
  color: #C0C4CC;
}

.class-name {
  font-weight: 600;
}

.mono {
  font-family: Consolas, Monaco, monospace;
}

.color-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  border: 1px solid #DCDFE6;
  display: inline-block;
}

.form-hint {
  color: #909399;
  font-size: 12px;
  margin-left: 8px;
}

.ellipsis-cell {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

</style>
